import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Star, Crown, Users, Globe, Bot } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';
import BlockyCharacter3D from '../components/BlockyCharacter3D';

// Stage icons mapping
const STAGE_DATA = {
  1: { icon: "🏕️", name: "Newcomer", color: "#FF6B6B" },
  2: { icon: "🗺️", name: "Explorer", color: "#FFD93D" },
  3: { icon: "🏗️", name: "Builder", color: "#6BCB77" },
  4: { icon: "⭐", name: "Master", color: "#4ECDC4" },
  5: { icon: "🏆", name: "Champion", color: "#A66CFF" },
};

const LeaderboardPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [neighbours, setNeighbours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('neighbours'); // 'neighbours' or 'global'

  useEffect(() => {
    fetchLeaderboards();
  }, []);

  const fetchLeaderboards = async () => {
    try {
      const token = localStorage.getItem('typetown_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch both leaderboards
      const [globalRes, neighboursRes] = await Promise.all([
        axios.get(`${API}/leaderboard/global?limit=20`),
        token ? axios.get(`${API}/leaderboard/neighbours`, { headers }) : Promise.resolve({ data: [] })
      ]);
      
      setLeaderboard(globalRes.data);
      setNeighbours(neighboursRes.data);
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-sunny-yellow fill-sunny-yellow" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="font-display text-lg text-text-medium">{rank}</span>;
  };

  const getStageDisplay = (stage) => {
    const data = STAGE_DATA[stage] || STAGE_DATA[1];
    return (
      <div className="flex items-center gap-1">
        <span className="text-lg">{data.icon}</span>
        <span className="font-semibold text-sm" style={{ color: data.color }}>{data.name}</span>
      </div>
    );
  };

  const renderLeaderboardTable = (data, showAiBadge = false) => (
    <div className="bg-white border-3 border-mint-green/30 rounded-2xl overflow-hidden shadow-lg" data-testid="leaderboard-table">
      {/* Table Header */}
      <div className="bg-gradient-to-r from-mint-green/20 to-lavender/20 px-6 py-3 border-b border-mint-green/20 grid grid-cols-12 items-center">
        <span className="font-display text-xs text-text-medium col-span-1">RANK</span>
        <span className="font-display text-xs text-text-medium col-span-5">PLAYER</span>
        <span className="font-display text-xs text-text-medium col-span-3">STAGE</span>
        <span className="font-display text-xs text-text-medium col-span-3 text-right">ACCURACY</span>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <div className="text-mint-green animate-pulse font-display text-xl">Loading...</div>
        </div>
      ) : data.length === 0 ? (
        <div className="p-8 text-center">
          <Star className="w-12 h-12 text-sunny-yellow mx-auto mb-4" />
          <p className="font-body text-text-medium">No rankings yet. Start typing to compete!</p>
        </div>
      ) : (
        <div>
          {data.map((entry, index) => {
            const isCurrentUser = entry.is_you || entry.user_id === user?.id;
            const isAi = entry.is_ai;
            return (
              <div
                key={entry.user_id || index}
                className={`px-6 py-4 grid grid-cols-12 items-center border-b border-soft-gray/50 last:border-0 transition-all ${
                  isCurrentUser 
                    ? 'bg-gradient-to-r from-sunny-yellow/20 to-mint-green/20 border-l-4 border-l-sunny-yellow' 
                    : index % 2 === 0 ? 'bg-cream/30' : 'bg-white'
                } ${isAi ? 'hover:bg-lavender/10' : ''}`}
                data-testid={`leaderboard-row-${index}`}
              >
                {/* Rank */}
                <div className="col-span-1 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                
                {/* Player */}
                <div className="col-span-5 flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-md text-sm"
                    style={{ backgroundColor: entry.pixel_color || '#4ECDC4' }}
                  >
                    {isAi ? '🤖' : entry.username}
                  </div>
                  <div className="flex flex-col">
                    <span className={`font-body font-semibold ${isCurrentUser ? 'text-sunny-yellow' : 'text-text-dark'}`}>
                      {isAi ? entry.username : 'Player'}
                      {isCurrentUser && <span className="text-mint-green text-xs ml-2">(You!)</span>}
                    </span>
                    {showAiBadge && isAi && (
                      <span className="text-xs text-lavender flex items-center gap-1">
                        <Bot className="w-3 h-3" /> AI Neighbour
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Stage */}
                <div className="col-span-3">
                  {getStageDisplay(entry.stage)}
                </div>
                
                {/* Accuracy */}
                <div className="col-span-3 text-right">
                  <span className={`font-display text-xl ${
                    entry.accuracy >= 95 ? 'text-mint-green' :
                    entry.accuracy >= 85 ? 'text-sunny-yellow' :
                    entry.accuracy >= 70 ? 'text-coral-orange' :
                    'text-text-medium'
                  }`}>
                    {entry.accuracy}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream via-warm-white to-mint-green/10">
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b-3 border-mint-green/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-text-medium hover:text-mint-green transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-body font-semibold">Back</span>
          </Link>
          
          <div className="font-display text-xl">
            <span className="text-mint-green">Type</span>
            <span className="text-sunny-yellow">Town</span>
            <span className="text-lavender ml-2">LEADERBOARD</span>
          </div>
          
          <div className="w-20" />
        </div>
      </nav>
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-sunny-yellow to-coral-orange rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-display text-4xl text-text-dark mb-2" data-testid="leaderboard-title">
              TypeTown Rankings
            </h1>
            <p className="font-body text-text-medium">See how you compare with neighbours and the world!</p>
          </div>
          
          {/* Tab Switcher */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={() => setActiveTab('neighbours')}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-display font-bold transition-all ${
                activeTab === 'neighbours'
                  ? 'bg-gradient-to-r from-lavender to-mint-green text-white shadow-lg'
                  : 'bg-white text-text-medium hover:bg-lavender/20 border-2 border-lavender/30'
              }`}
              data-testid="neighbours-tab"
            >
              <Users className="w-5 h-5" />
              My Neighbours
            </button>
            <button
              onClick={() => setActiveTab('global')}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-display font-bold transition-all ${
                activeTab === 'global'
                  ? 'bg-gradient-to-r from-sunny-yellow to-coral-orange text-white shadow-lg'
                  : 'bg-white text-text-medium hover:bg-sunny-yellow/20 border-2 border-sunny-yellow/30'
              }`}
              data-testid="global-tab"
            >
              <Globe className="w-5 h-5" />
              Global Rankings
            </button>
          </div>
          
          {/* Tab Description */}
          <div className="text-center mb-6 p-4 bg-white/70 rounded-2xl border-2 border-dashed border-lavender/30">
            {activeTab === 'neighbours' ? (
              <p className="text-text-medium font-body">
                🏘️ <strong>Neighbours</strong> are friendly AI competitors! Beat them to climb the ranks and prove you're the best typer in the neighbourhood!
              </p>
            ) : (
              <p className="text-text-medium font-body">
                🌍 <strong>Global Rankings</strong> show real players from around the world. Complete 5+ sessions to appear here!
              </p>
            )}
          </div>
          
          {/* Leaderboard Table */}
          {activeTab === 'neighbours' 
            ? renderLeaderboardTable(neighbours, true)
            : renderLeaderboardTable(leaderboard, false)
          }
          
          {/* Stage Legend */}
          <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-mint-green/20 shadow-md">
            <h3 className="font-display text-lg text-text-dark mb-4 text-center">Stage Icons</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {Object.entries(STAGE_DATA).map(([stage, data]) => (
                <div 
                  key={stage} 
                  className="flex items-center gap-2 px-4 py-2 rounded-xl"
                  style={{ backgroundColor: `${data.color}20`, border: `2px solid ${data.color}40` }}
                >
                  <span className="text-2xl">{data.icon}</span>
                  <span className="font-semibold text-sm" style={{ color: data.color }}>{data.name}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Back to Practice */}
          <div className="mt-8 text-center">
            <Link
              to="/island"
              className="font-display uppercase tracking-wider bg-gradient-to-r from-mint-green to-sky-blue hover:from-sky-blue hover:to-mint-green text-white px-10 py-4 text-xl rounded-2xl shadow-lg hover:shadow-xl transition-all inline-block"
              data-testid="practice-cta"
            >
              🏝️ Back to TypeTown Island
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeaderboardPage;
